import { orderBy } from "lodash";
import { DateTime } from "luxon";
import { StringParam, useQueryParam, withDefault } from "next-query-params";
import { useMemo, useState } from "react";

import { JobCard } from "../job-card/job-card";
import pluralize from "pluralize";
import { When } from "../../../../../components/when";
import { ThreeColumn } from "../../../../../components/layouts/three-column";
import Buttons from "../../../../../components/button";
import { FiChevronDown } from "react-icons/fi";
import { Select } from "../../../../../components/select/select";
import { PublicCandidateJobAppNode } from "../../../../graphql.types";
import {
  ClientJobCardListItem,
  JobListSortType,
} from "../../types/client-job-card-list-item";

const stringSearch = (value: string, searchValue: string) => {
  return value.toLocaleUpperCase().includes(searchValue.toLocaleUpperCase());
};

const LABELS = {
  [JobListSortType.mostRecent]: "Most Recent",
  [JobListSortType.oldest]: "Oldest",
  [JobListSortType.closeDate]: "Close Date",
};

const formatDate = (dt: string) => {
  try {
    const date = DateTime.fromISO(dt);
    if (date.isValid) {
      return `Close Date: ${DateTime.fromISO(dt).toLocaleString(
        DateTime.DATE_FULL,
      )}`;
    } else {
      return "";
    }
  } catch (e) {
    return "";
  }
};

// JobCardList is a shared component that handles the sorting, filtering, searching
// logic for the job list. This is shared with candidates and agencies.
// please keep this component as view only, meaning do not add any queries or logic specific
// to a particular user type (agency / candidate).
export const JobCardList = (props: {
  jobs: ClientJobCardListItem[];
  jobApps?: PublicCandidateJobAppNode[];
  sort?: JobListSortType;
  onSortChange?: (sort: JobListSortType) => void;
  isFiltered?: boolean;
  isLoading?: boolean;
  onJobClick: (job: ClientJobCardListItem) => void;
}) => {
  const jobs = props.jobs;

  const sorted = useMemo(() => {
    if (props.sort === JobListSortType.mostRecent) {
      return orderBy(jobs, ["openDate"], ["desc"]);
    } else if (props.sort === JobListSortType.oldest) {
      return orderBy(jobs, ["openDate"], ["asc"]);
    } else if (props.sort === JobListSortType.closeDate) {
      return orderBy(jobs, ["closeDate"], ["asc"]);
    } else {
      return orderBy(jobs, ["openDate"], ["desc"]);
    }
  }, [jobs, props.sort]);

  const [searchValue] = useQueryParam("search", withDefault(StringParam, ""));
  const filtered = sorted.filter((s) =>
    stringSearch(s?.title ?? "", searchValue),
  );
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div>
      <ThreeColumn.Container className="items-center justify-between pb-4">
        <ThreeColumn.Content>
          <h1 className="text-charcoal pl-1 text-base font-medium">
            {pluralize("Job", filtered.length, true)} Found
          </h1>
        </ThreeColumn.Content>
        <ThreeColumn.Trailing>
          <Select
            options={[
              {
                label: LABELS[JobListSortType.mostRecent],
                value: JobListSortType.mostRecent,
              },
              {
                label: LABELS[JobListSortType.oldest],
                value: JobListSortType.oldest,
              },
              {
                label: LABELS[JobListSortType.closeDate],
                value: JobListSortType.closeDate,
              },
            ]}
            isItemSelected={(i) => {
              return i.value === props.sort;
            }}
            open={isOpen}
            onSelectItem={(item) => {
              props.onSortChange(item.value as JobListSortType);
              setIsOpen(false);
            }}
            onOpenChange={(a) => setIsOpen(a)}
          >
            <Buttons.Base
              variant="base"
              className="text-light-elephant text-sm"
            >
              Sort By: {LABELS[props.sort]}
              <FiChevronDown size={16} />
            </Buttons.Base>
          </Select>
        </ThreeColumn.Trailing>
      </ThreeColumn.Container>
      <When
        condition={
          (props.isFiltered && filtered.length === 0 && !props.isLoading) ||
          (searchValue !== "" && filtered.length === 0 && !props.isLoading)
        }
      >
        <div className="flex flex-col items-center gap-y-3 pt-20">
          <img
            src="/v2/document-search.svg"
            alt=""
            className="w-[110px] grayscale"
          />
          <p className="text-charcoal text-2xl font-medium">
            There are no jobs that match your filter criteria.
          </p>
        </div>
      </When>

      <When condition={props.isLoading}>
        <div className="flex items-center justify-center pt-20">Loading</div>
      </When>

      <When condition={filtered.length > 0 && !props.isLoading}>
        <ul aria-label="Job Results" className="flex flex-col gap-y-3 pb-10">
          {filtered.map((job: ClientJobCardListItem) => (
            <JobCard
              key={job.id}
              title={job.title}
              // Assume that we will always have the date
              // assert that it is a string
              openDate={DateTime.fromISO(job.openDate as string).toJSDate()}
              jobApp={(props.jobApps ?? []).find((a) => a.jobId === job.id)}
              onClick={() => props.onJobClick(job)}
              details={[
                ...(job?.departments ?? []).map((d) => d.name),
                ...(job?.jobOffices ?? []).map((d) => d.name),
                ...(job?.positionTypes ?? []).map((d) => d.name),
                ...(job?.jobEmploymentTypes ?? []).map((d) => d.employmentType),
                formatDate(job?.closeDate ?? ""),
              ].filter((s) => s !== "")}
            />
          ))}
        </ul>
      </When>
    </div>
  );
};
